import React, { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Power, Loader2, KeyRound, FlaskConical } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/DashLayout";

export default function SaaSPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Platform Gateway State
  const [platConfig, setPlatConfig] = useState(null);
  const [platForm, setPlatForm] = useState({ key_id: "", key_secret: "", webhook_secret: "" });
  const [platSaving, setPlatSaving] = useState(false);
  const [platTesting, setPlatTesting] = useState(false);
  const [showPlatSecrets, setShowPlatSecrets] = useState(false);

  // Plan Modal State
  const [modal, setModal] = useState(null); // null | 'add' | plan object
  const [form, setForm] = useState({ name: "", amount: 0, duration_days: 30, status: "active" });

  const loadData = async () => {
    setLoading(true);
    try {
      const [plansRes, platRes] = await Promise.all([
        api.get("/superadmin/saas-plans"),
        api.get("/superadmin/razorpay/platform-config").catch(() => ({ data: null }))
      ]);
      setPlans(plansRes.data || []);
      
      if (platRes.data) {
        setPlatConfig(platRes.data);
        if (platRes.data.configured) {
          setPlatForm({ key_id: platRes.data.key_id || "", key_secret: "", webhook_secret: "" });
        }
      }
    } catch {
      toast.error("Failed to load SaaS plans data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // ── Gateway Config Actions ──
  const savePlatConfig = async (e) => {
    e.preventDefault();
    if (!platForm.key_id) { toast.error("Key ID is required"); return; }
    setPlatSaving(true);
    try {
      const payload = { key_id: platForm.key_id };
      payload.key_secret = platForm.key_secret || (platConfig?.key_secret ? undefined : "");
      if (platForm.key_secret) payload.key_secret = platForm.key_secret;
      else if (!platConfig?.configured) { toast.error("Key Secret is required"); setPlatSaving(false); return; }
      else {
        toast.error("Please re-enter the Key Secret to save changes");
        setPlatSaving(false);
        return;
      }
      payload.webhook_secret = platForm.webhook_secret || null;
      await api.put("/superadmin/razorpay/platform-config", payload);
      toast.success("Platform Razorpay config saved!");
      loadData();
      setPlatForm(p => ({ ...p, key_secret: "", webhook_secret: "" }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save config");
    } finally { setPlatSaving(false); }
  };

  const testPlatCredentials = async () => {
    setPlatTesting(true);
    try {
      const { data } = await api.post("/superadmin/razorpay/test-platform-credentials");
      if (data.ok) toast.success(data.message || "Platform credentials are valid ✓");
      else toast.error(data.message || "Invalid credentials");
    } catch { toast.error("Test failed"); }
    finally { setPlatTesting(false); }
  };

  // ── Plan Actions ──
  const openAdd = () => {
    setForm({ name: "", amount: 0, duration_days: 30, status: "active" });
    setModal("add");
  };

  const openEdit = (p) => {
    setForm({ name: p.name, amount: p.amount, duration_days: p.duration_days, status: p.status });
    setModal(p);
  };

  const savePlan = async (e) => {
    e.preventDefault();
    try {
      if (modal === "add") {
        await api.post("/superadmin/saas-plans", form);
        toast.success("Plan created successfully");
      } else {
        await api.put(`/superadmin/saas-plans/${modal.id}`, form);
        toast.success("Plan updated successfully");
      }
      setModal(null);
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save plan");
    }
  };

  const deletePlan = async (id) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;
    try {
      await api.delete(`/superadmin/saas-plans/${id}`);
      toast.success("Plan deleted");
      loadData();
    } catch {
      toast.error("Failed to delete plan");
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="font-heading font-bold text-2xl text-brand-nav flex items-center gap-2">
          <KeyRound className="w-6 h-6 text-brand-primary" /> SaaS Subscription Plans
        </h3>
        <p className="text-slate-500 mt-1">Manage the subscription plans offered to your Admins (LCOs).</p>
      </div>

      {/* Gateway Config Section */}
      <div className="mb-8 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading font-bold text-xl text-indigo-900 flex items-center gap-2">
              <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              SaaS Payment Gateway
            </h3>
            <p className="text-sm text-indigo-600/70 mt-0.5">Configure the Razorpay account where Admin SaaS subscription payments will be received.</p>
          </div>
          {platConfig?.configured && (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-green-100 text-green-700">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Configured
            </span>
          )}
        </div>

        <form onSubmit={savePlatConfig} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-indigo-800 mb-1">Razorpay Key ID</label>
              <input required value={platForm.key_id} onChange={e => setPlatForm(p => ({ ...p, key_id: e.target.value }))}
                className="w-full border border-indigo-200 rounded-lg px-3 py-2 text-sm font-mono bg-white focus:ring-2 focus:ring-indigo-300 focus:outline-none"
                placeholder="rzp_test_XXXXXXXXXX" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-indigo-800 mb-1">Razorpay Key Secret</label>
              <input required={!platConfig?.configured} value={platForm.key_secret}
                onChange={e => setPlatForm(p => ({ ...p, key_secret: e.target.value }))}
                type={showPlatSecrets ? "text" : "password"}
                className="w-full border border-indigo-200 rounded-lg px-3 py-2 text-sm font-mono bg-white focus:ring-2 focus:ring-indigo-300 focus:outline-none"
                placeholder={platConfig?.configured ? "(re-enter to update)" : "your_key_secret"} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-indigo-800 mb-1">Webhook Secret</label>
              <input value={platForm.webhook_secret} onChange={e => setPlatForm(p => ({ ...p, webhook_secret: e.target.value }))}
                type={showPlatSecrets ? "text" : "password"}
                className="w-full border border-indigo-200 rounded-lg px-3 py-2 text-sm font-mono bg-white focus:ring-2 focus:ring-indigo-300 focus:outline-none"
                placeholder={platConfig?.webhook_secret ? "(set — re-enter to change)" : "optional webhook secret"} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-indigo-800 mb-1">Webhook URL</label>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <input readOnly value={`${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api/webhooks/razorpay/platform`}
                className="flex-1 border border-indigo-200 rounded-lg px-3 py-2 font-mono text-xs bg-indigo-50/50 text-indigo-700" />
              <button type="button" onClick={() => {
                navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api/webhooks/razorpay/platform`);
                toast.success('Webhook URL copied!');
              }} className="px-3 py-2 text-xs font-semibold text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors">
                Copy
              </button>
            </div>
            <p className="text-xs text-indigo-600/70 mt-1">Configure this URL in your Platform Razorpay Dashboard to receive subscription updates.</p>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-indigo-700 cursor-pointer select-none">
              <input type="checkbox" checked={showPlatSecrets} onChange={e => setShowPlatSecrets(e.target.checked)} className="rounded" />
              Show secrets
            </label>
            <div className="flex gap-2">
              {platConfig?.configured && (
                <button type="button" onClick={testPlatCredentials} disabled={platTesting}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 border border-indigo-200 hover:bg-indigo-100 px-4 py-2 rounded-lg transition-colors disabled:opacity-50">
                  {platTesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <FlaskConical className="w-3 h-3" />}
                  Test Credentials
                </button>
              )}
              <button type="submit" disabled={platSaving}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 px-5 py-2 rounded-lg transition-colors disabled:opacity-50">
                {platSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                Save Gateway Config
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Plans Section */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h4 className="font-heading font-bold text-lg text-brand-nav">Available Plans</h4>
          <button onClick={openAdd} className="bg-brand-primary hover:bg-brand-primaryHover text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
            <Plus className="w-4 h-4" /> Add Plan
          </button>
        </div>
        
        {loading ? (
          <div className="p-12 text-center text-slate-500 flex flex-col items-center">
            <Loader2 className="w-6 h-6 animate-spin mb-2" />
            Loading plans...
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-6 py-3">Plan Name</th>
                <th className="px-6 py-3">Amount (₹)</th>
                <th className="px-6 py-3">Duration (Days)</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {plans.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-slate-500">No plans found.</td></tr>
              )}
              {plans.map(p => (
                <tr key={p.id} className="hover:bg-slate-50/50">
                  <td className="px-6 py-4 font-semibold text-brand-nav">{p.name}</td>
                  <td className="px-6 py-4 font-bold text-brand-accent">₹{p.amount.toLocaleString()}</td>
                  <td className="px-6 py-4 text-slate-600">{p.duration_days} Days</td>
                  <td className="px-6 py-4">
                    <Badge tone={p.status === "active" ? "success" : "neutral"}>{p.status}</Badge>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(p)} className="p-1.5 text-brand-primary hover:bg-brand-primary/10 rounded transition-colors" title="Edit">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => deletePlan(p.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Plan Modal */}
      {modal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setModal(null)}>
          <form onSubmit={savePlan} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h4 className="font-heading font-bold text-xl text-brand-nav border-b pb-4 mb-4">
              {modal === "add" ? "Create New Plan" : "Edit Plan"}
            </h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Plan Name</label>
                <input required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-primary/20"
                  placeholder="e.g. Annual Premium" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Amount (₹)</label>
                  <input required type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: Number(e.target.value) }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-primary/20" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Duration (Days)</label>
                  <input required type="number" min="1" value={form.duration_days} onChange={e => setForm(p => ({ ...p, duration_days: Number(e.target.value) }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-primary/20" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-primary/20">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-slate-100">
              <button type="button" onClick={() => setModal(null)} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 rounded-lg transition-colors">Cancel</button>
              <button type="submit" className="px-4 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primaryHover rounded-lg transition-colors">
                {modal === "add" ? "Create Plan" : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
